<template>
  <MunicipalityDropdown @municipalityChanged="onMunicipalityChanged" />
  <ElectoralAlliancesModal
    @electoralAlliancesChanged="onElectoralAlliancesChanged"
    :parties="parties"
    :alliances="alliances"
  />

  <div style="margin-bottom: 30px;">
    <button
      tabindex="-1"
      type="button"
      class="btn btn-dark"
      data-bs-toggle="modal"
      data-bs-target="#municipalityModal"
    >
      Indlæs kommunale valgresultater
    </button>

    <button
      tabindex="-1"
      type="button"
      class="btn btn-dark btn-margin-left"
      data-bs-toggle="modal"
      data-bs-target="#electoralAllianceModal"
    >
      Valgforbund
    </button>

    <button class="btn btn-default btn-margin-left" @click="reset">
      Nulstil
    </button>
  </div>

  <div class="card">
    <div class="card-body">
      <div class="row">
        <div class="col-md-6 has-validation">
          <label for="title" class="form-label">Titel</label>
          <input
            type="text"
            name="title"
            id="title"
            class="form-control"
            placeholder="Titel"
            v-model="title"
            :class="{ 'is-invalid': !validateTitle() }"
            ref="title"
          />
          <div class="invalid-feedback">
            Udfyld titel
          </div>
        </div>
        <div class="col-md-2 has-validation">
          <label for="availableSeats" class="form-label"
            >Mandater til fordeling</label
          >
          <input
            type="text"
            v-mask="'###'"
            name="availableSeats"
            id="availableSeats"
            class="form-control"
            placeholder="Mandater til fordeling"
            :class="{ 'is-invalid': !validateSeats() }"
            v-model.number="availableSeats"
          />
          <div class="invalid-feedback">
            Udfyld antal
          </div>
        </div>
        <div class="col-md-2">
          <label for="threshold" class="form-label">Spærregrænse (%)</label>
          <input
            type="text"
            v-mask="'##'"
            name="threshold"
            id="threshold"
            class="form-control"
            placeholder="Spærregrænse (%)"
            v-model.number="threshold"
          />
        </div>
      </div>

      <div style="margin-top: 30px;">
        <VoteListItem
          v-for="(result, resultIndex) in voteResults"
          v-bind:key="resultIndex"
          :partyLetter="result.partyLetter"
          :party="result.party"
          :voteCount="result.voteCount"
          :rowId="resultIndex"
          @removed="onListItemRemoved"
          @inputchanged="onVoteListItemChanged"
        />
      </div>

      <p class="mt-3" v-if="usingMunicipalData">
        Stemmeantal er hentet via KMD Valg.
      </p>

      <div class="mt-3">
        <button
          type="button"
          class="btn btn-success"
          :disabled="!validateForm()"
          @click="calculateResult()"
        >
          Beregn mandatfordeling
        </button>
        <button
          class="btn btn-success btn-margin-left"
          :disabled="isExporting"
          v-if="simpleResult"
          @click="exportToExcel()"
        >
          <span
            v-show="isExporting"
            class="spinner-border spinner-border-sm"
          ></span>
          <span>Eksporter</span>
        </button>

        <button
          type="button"
          @click="addResult()"
          class="btn btn-default btn-margin-left"
          title="Tilføj række"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            class="bi bi-plus-square"
            viewBox="0 0 16 16"
          >
            <path
              d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"
            />
            <path
              d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>

  <h3 class="mt-4" v-if="simpleResult">{{ title }}</h3>

  <SimpleResult
    v-if="simpleAllianceResult"
    :threshold="threshold"
    :show-electoral-alliances="true"
    :items="simpleAllianceResult.items"
  />

  <SimpleResult
    v-if="simpleResult"
    :threshold="threshold"
    :show-electoral-alliances="false"
    :items="simpleResult.items"
  />

  <div v-if="simpleResult && !showDetailed">
    <button class="btn btn-dark" @click="showDetailedResult()">
      Vis detaljeret mandatfordeling
    </button>
  </div>

  <DetailedResult
    v-if="detailedResult && showDetailed"
    :threshold="threshold"
    :title="title"
    :items="detailedResult.items"
  />
</template>

<script>
import { HTTP } from '../http-common.js';
import VoteListItem from './VoteListItem.vue';
import MunicipalityDropdown from './MunicipalityDropdown.vue';
import SimpleResult from './SimpleResult.vue';
import DetailedResult from './DetailedResult.vue';
import { mask } from 'vue-the-mask';
import ElectoralAlliancesModal from './ElectoralAlliancesModal.vue';

export default {
  name: 'VoteList',
  components: {
    VoteListItem,
    MunicipalityDropdown,
    SimpleResult,
    DetailedResult,
    ElectoralAlliancesModal,
  },
  directives: { mask },
  data: function() {
    return {
      title: '',
      threshold: undefined,
      availableSeats: undefined,
      electoralAlliances: [],
      voteResults: [
        { party: undefined, partyLetter: '', voteCount: undefined },
        { party: undefined, partyLetter: '', undefined },
      ],
      simpleResult: undefined,
      detailedResult: undefined,
      simpleAllianceResult: undefined,
      showDetailed: false,
      usingMunicipalData: false,
      parties: [],
      alliances: [],
      isExporting: false,
    };
  },
  methods: {
    validateTitle: function() {
      if (this.title.length < 3) {
        return false;
      }
      return true;
    },
    validateVotes: function() {
      return true;
    },
    validateSeats: function() {
      if (!this.availableSeats) return false;

      if (this.availableSeats <= 0) {
        return false;
      }
      return true;
    },
    validateForm: function() {
      return (
        this.validateTitle() && this.validateSeats() && this.validateVotes()
      );
    },
    showDetailedResult: function() {
      this.showDetailed = !this.showDetailed;
    },
    calculateResult: function() {
      var votes = this.voteResults.map(function(element) {
        return {
          partyLetter: element.partyLetter,
          party: element.party,
          votes: element.voteCount,
        };
      });

      var alliances = this.electoralAlliances.map(function(element) {
        return {
          name: element.name,
          parties: element.parties
            .filter((p) => p.checked)
            .map((p) => p.letter),
        };
      });

      var request = {
        title: this.title,
        availableSeats: this.availableSeats,
        threshold: this.threshold,
        votes: votes,
        alliances: alliances,
      };

      HTTP.post('/calculations/calc-v2', request)
        .then((response) => {
          this.simpleResult = response.data.simpleDistribution;
          this.detailedResult = response.data.detailedDistribution;
          this.simpleAllianceResult =
            response.data.simpleDistributionWithAlliances;
        })
        .catch((error) => {
          console.error(JSON.stringify(error));
        });
    },
    addResult: function() {
      if (this.voteResults.length == 50) {
        alert('Du kan ikke tilføje flere lister.');
        return;
      }

      this.voteResults.push({
        party: undefined,
        partyLetter: '',
        voteCount: undefined,
      });
    },
    onMunicipalityChanged: function(municipalityCode) {
      this.voteResults = [];
      this.simpleResult = undefined;
      this.detailedResult = undefined;
      this.simpleAllianceResult = undefined;

      if (municipalityCode === '') return;

      HTTP.get('/votes/municipality/' + municipalityCode)
        .then((response) => {
          this.title = response.data.municipality;
          const results = response.data.results.map((item) => ({
            party: item.party,
            partyLetter: item.partyLetter,
            voteCount: item.votes,
          }));

          this.parties = [];
          results.forEach((result) => {
            this.parties.push({
              letter: result.partyLetter,
              name: result.party,
              checked: false,
            });
          });

          this.voteResults = results;
          this.usingMunicipalData = true;
        })
        .catch(() =>
          alert('Der opstod en fejl under indlæsning af kommunale valgdata.')
        );
    },
    onElectoralAlliancesChanged: function(alliances) {
      this.electoralAlliances = alliances;
    },
    onListItemRemoved: function(id) {
      if (this.voteResults.length == 2) {
        alert(
          'Der skal være mindst to partier/lister for at kunne foretage beregningen.'
        );
        return;
      }

      this.voteResults.splice(id, 1);
    },
    onVoteListItemChanged: function(item) {
      this.voteResults[item.rowId].partyLetter = item.partyLetter;
      this.voteResults[item.rowId].voteCount = item.voteCount;

      this.parties = this.voteResults
        .filter((x) => !!x)
        .map((v) => ({
          letter: v.partyLetter,
          name: v.partyLetter,
          checked: false,
        }));
    },
    exportToExcel: function() {
      this.isExporting = true;
      var votes = this.voteResults.map(function(element) {
        return {
          partyLetter: element.partyLetter,
          party: element.party,
          votes: element.voteCount,
        };
      });

      var alliances = this.electoralAlliances.map(function(element) {
        return {
          name: element.name,
          parties: element.parties
            .filter((p) => p.checked)
            .map((p) => p.letter),
        };
      });

      var request = {
        title: this.title,
        availableSeats: this.availableSeats,
        threshold: this.threshold,
        votes: votes,
        alliances: alliances,
      };

      HTTP.post('/calculations/export', request)
        .then(() => {
          alert('Du modtager snarest de eksporterede data pr. e-mail.');
        })
        .catch(() => alert('Der opstod en fejl. Data blev ikke eksporteret.'))
        .finally(() => {
          this.isExporting = false;
        });
    },
    reset: function() {
      this.voteResults = [];
      this.electoralAlliances = [];
      this.alliances = [];
      this.parties = [];
      this.threshold = undefined;
      this.title = '';
      this.availableSeats = undefined;
      this.usingMunicipalData = false;
      this.simpleResult = undefined;
      this.detailedResult = undefined;
      this.simpleAllianceResult = undefined;
      this.voteResults.push({
        party: '',
        partyLetter: '',
        voteCount: undefined,
      });
      this.voteResults.push({
        party: '',
        partyLetter: '',
        voteCount: undefined,
      });

      this.$refs.title.focus();
    },
  },
};
</script>

<style scoped>
.btn-margin-left {
  margin-left: 15px;
}
</style>
